import React, { useMemo } from 'react';
import { showHTML } from '../../helpers/editorHelpers';
import Icon from '../Icon';

const noop = () => {};

const fallbackCopyText = (text) => {
  if (typeof document === 'undefined') return;
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.right = '0';
    textarea.style.bottom = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    document.execCommand('copy');
    document.body.removeChild(textarea);
  } catch (err) {
    // silent fallback
  }
};

const copySelection = () => {
  if (typeof window === 'undefined') return;
  const text = window.getSelection?.()?.toString?.();
  if (!text) return;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopyText(text));
    return;
  }
  fallbackCopyText(text);
};

const buildViewModeItems = ({ viewMode, setViewMode, setContent, editor }) => ([
  {
    label: 'Rich Text',
    iconClass: 'text-aa',
    onClick: () => setViewMode('rich'),
    isActive: () => viewMode === 'rich'
  },
  {
    label: 'HTML',
    iconClass: 'code',
    onClick: () => {
      setViewMode('html');
      try {
        showHTML(editor, setContent);
      } catch (err) {
        // ignore
      }
    },
    isActive: () => viewMode === 'html'
  }
]);

export default function ReadOnlyToolBar({
  id = 'toolbar',
  variant = 'readOnly',
  editor,
  viewMode = 'rich',
  setViewMode = noop,
  setContent = noop,
  onCompareVersion,
  onDiffVersion,
  onRestoreVersion,
  onRestoreVersionCopy
}) {
  const toolbarItems = useMemo(() => {
    const compareHandler = onCompareVersion || onDiffVersion || noop;
    const restoreHandler = onRestoreVersion || noop;
    const restoreCopyHandler = onRestoreVersionCopy || noop;
    const items = [
      {
        label: 'Copy',
        iconClass: 'copy',
        onClick: copySelection,
        isActive: () => false
      }
    ];

    if (variant === 'version') {
      items.push({ type: 'divider' });
      items.push(
        {
          label: 'Compare',
          iconClass: 'git-diff',
          onClick: compareHandler,
          isActive: () => false
        },
        {
          label: 'Restore',
          iconClass: 'git-merge',
          onClick: restoreHandler,
          isActive: () => false
        },
        {
          label: 'Restore as a copy',
          iconClass: 'git-fork',
          onClick: restoreCopyHandler,
          isActive: () => false
        }
      );
    }

    items.push({ type: 'divider' });
    items.push(...buildViewModeItems({ viewMode, setViewMode, setContent, editor }));
    return items;
  }, [variant, viewMode, setViewMode, setContent, editor, onCompareVersion, onDiffVersion, onRestoreVersion, onRestoreVersionCopy]);

  const nodes = toolbarItems.map((item, index) => {
    if (!item) return null;
    if (item.type === 'divider') {
      return <div key={`d-${index}`} className="toolbar-divider" />;
    }
    const activeClass = typeof item.isActive === 'function' && item.isActive(editor) ? 'active' : '';
    return (
      <button
        key={item.label || index}
        className={`iconButton ${activeClass}`.trim()}
        onMouseDown={e => e.preventDefault()}
        onClick={item.onClick}
        aria-label={item.label}
        data-tooltip={item.label}
        data-tooltip-placement="top"
      >
        <Icon icon={item.iconClass} className={`icon ${String(item.iconClass || '').replace(/^ph[- ]?/, '').trim()}`} />
      </button>
    );
  });

  return (
    <div
      id={id}
      onMouseDown={e => e.preventDefault()}
      onTouchStart={e => e.preventDefault()}
      tabIndex={-1}
    >
      <div className="toolbar-inner" role="toolbar" aria-label="Read-only toolbar">
        {nodes}
      </div>
    </div>
  );
}

